import { Navigate, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '../../../components/Button';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useMediaQuery } from '../../../lib/use-media-query';
import { useCanEdit, useCanView } from '../../../stores/session';
import { usePipelineTemplates } from '../api/jobs-api';
import { PipelineEditor } from '../components/admin-setup/PipelineEditor';
import { PipelineList, PipelinePicker } from '../components/admin-setup/PipelineList';
import styles from './PipelinesPage.module.css';

/**
 * ADMIN-01 (F19.01), Pipelines A (Admin redesign batch 2): the pipelines on the left, the open one beside them. One per
 * role family; new jobs copy the one that matches, or the default. Editing a pipeline never moves a job that already
 * copied it (non-negotiable #4).
 *
 * `/admin/pipelines` opens the first pipeline (the default comes first); `/admin/pipelines/:id` opens that one, and
 * `/admin/pipelines/new` a new one.
 */

/** Laptop width and below: the list becomes a drop-down above the editor. */
const COMPACT_MEDIA = '(max-width: 1180px)';
export const NEW_PIPELINE = 'new';

export function PipelinesPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const pipelines = usePipelineTemplates(canView);
  if (!canView) return <ForbiddenState resource="pipelines" contact="Your admin" />;
  const first = pipelines.data?.[0];
  if (first) return <Navigate to="/admin/pipelines/$pipelineId" params={{ pipelineId: first.id }} replace />;
  return <PipelinesWorkspace selectedId={null} />;
}

export function PipelinesWorkspace({ selectedId }: { readonly selectedId: string | null }): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const pipelines = usePipelineTemplates(canView);
  const isCompact = useMediaQuery(COMPACT_MEDIA);
  const navigate = useNavigate();

  if (!canView) return <ForbiddenState resource="pipelines" contact="Your admin" />;

  const rows = pipelines.data ?? [];
  const isNew = selectedId === NEW_PIPELINE;
  const open = (id: string): void => void navigate({ to: '/admin/pipelines/$pipelineId', params: { pipelineId: id } });
  const startNew = (): void => open(NEW_PIPELINE);
  const existing = rows.find((pipeline) => pipeline.id === selectedId) ?? null;

  const editor = (): ReactNode => {
    if (pipelines.isPending) return <SkeletonLines count={10} />;
    if (pipelines.isError && rows.length === 0) return <ServerErrorState onRetry={() => void pipelines.refetch()} />;
    if (isNew) {
      if (!canEdit) return <ForbiddenState resource="adding pipelines" contact="Your admin" isInset />;
      return (
        <PipelineEditor
          key={NEW_PIPELINE}
          latest={null}
          pipelines={rows}
          canEdit
          onCreated={(id) => void navigate({ to: '/admin/pipelines/$pipelineId', params: { pipelineId: id }, replace: true })}
          onCancelNew={() => void navigate({ to: '/admin/pipelines' })}
          onRefetch={() => void pipelines.refetch()}
        />
      );
    }
    if (existing) {
      return (
        <PipelineEditor
          key={existing.id}
          latest={existing}
          pipelines={rows}
          canEdit={canEdit}
          onCreated={open}
          onCancelNew={() => void navigate({ to: '/admin/pipelines' })}
          onRefetch={() => void pipelines.refetch()}
        />
      );
    }
    if (selectedId !== null) {
      return (
        <EmptyState
          isInset
          title="This pipeline doesn't exist"
          body="It may have been removed, or the link is wrong. Pick a pipeline from the list."
          action={
            <Button variant="outline" size="sm" onClick={() => void navigate({ to: '/admin/pipelines' })}>
              Open the first pipeline
            </Button>
          }
        />
      );
    }
    return (
      <EmptyState
        isInset
        title="No pipelines yet"
        body="Without one, every job starts from the standard stages and the names candidates see."
        action={
          canEdit ? (
            <Button variant="solid" size="sm" icon={Plus} onClick={startNew}>
              New pipeline
            </Button>
          ) : undefined
        }
      />
    );
  };

  const listedId = isNew ? null : selectedId;
  const list = (): ReactNode => {
    if (pipelines.isPending) return <SkeletonLines count={5} />;
    if (rows.length === 0) return null;
    return isCompact ? (
      <PipelinePicker pipelines={rows} selectedId={listedId} onSelect={open} />
    ) : (
      <PipelineList pipelines={rows} selectedId={listedId} onSelect={open} />
    );
  };

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Pipelines {!pipelines.isPending && <span className={styles.count}>{rows.length}</span>}
        </h1>
        {canEdit && rows.length > 0 && (
          <Button variant="solid" icon={Plus} disabled={isNew} onClick={startNew}>
            New pipeline
          </Button>
        )}
      </header>
      <p className={styles.lede}>
        One per role family. New jobs copy the one that matches, or the default. The order of stages is fixed; switch optional ones on or off and
        name each one.
      </p>
      <div className={styles.layout} data-has-list={rows.length > 0 || pipelines.isPending || undefined}>
        {list()}
        <div className={styles.editorColumn}>{editor()}</div>
      </div>
    </div>
  );
}
